import * as React from "react"
import { Link, useLocation } from "react-router-dom"
import tmLogo from "@/assets/imgs/tm-logo-v2.png"
import { useTranslation } from "react-i18next"

import { NavUser } from "@/components/ui/nav-user"
import { ProfileSettingsOverlay } from "@/components/ui/profile-settings-overlay"
import { useAuthStore } from "@/stores/auth-store"
import { useRBACStore } from "@/stores/rbac-store"
import { filterMenuByRoleAndUIMode, MenuItem } from "@/utils/rbac.utils"
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
} from "@/components/ui/sidebar"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"

// Types are now imported from rbac.utils

// Menu data structure
const data = {
  navMain: [
    {
      titleKey: "dashboard",
      url: "/dashboard",
      items: [],
    },
    {
      titleKey: "guideline.title",
      url: "/guideline",
      items: [],
    },
    {
      titleKey: "assessment.title",
      url: "#", // Make Assessment non-clickable
      items: [
        {
          titleKey: "assessment.ongoing",
          url: "/assessment?tab=ongoing",
        },
        {
          titleKey: "assessment.completed",
          url: "/assessment?tab=completed",
        },
        {
          titleKey: "assessment.archived",
          url: "/assessment?tab=archived",
        },
        {
          titleKey: "assessment.overallReport",
          url: "/assessment/compare",
        },
        {
          titleKey: "assessment.combinedReport",
          url: "/assessment/combined-report",
        },
      ],
    },
    {
      titleKey: "participants.title",
      url: "#",
      items: [
        {
          titleKey: "participants.search",
          url: "/participants/search",
        },
        {
          titleKey: "participants.priority",
          url: "/participants/priority",
        },
      ],
    },
    {
      titleKey: "organizationAdmin.title",
      url: "#",
      items: [
        {
          titleKey: "organizationAdmin.editOrg",
          url: "/organization/update",
        },
        {
          titleKey: "organizationAdmin.manageChampions",
          url: "/organization/manage-champions",
        },
        {
          titleKey: "organizationAdmin.manageGroups",
          url: "/group/list",
        },
      ],
    },
    {
      titleKey: "groupAdmin.title",
      url: "#",
      items: [
        {
          titleKey: "groupAdmin.manageChampions",
          url: "/organization/manage-champions",
        },
      ],
    },
    {
      titleKey: "terraceMetricsAdmin.title",
      url: "#",
      items: [
        {
          titleKey: "terraceMetricsAdmin.districtsList",
          url: "/organization/list",
        },
        {
          titleKey: "terraceMetricsAdmin.adminUsers",
          url: "/admin/users",
        },
        {
          titleKey: "terraceMetricsAdmin.indicatorList",
          url: "/admin/indicators",
        },
      ],
    },
  ] as MenuItem[],
}

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { t } = useTranslation('navigation');
  const { user, logout } = useAuthStore()
  const { isLoading: rbacLoading, userRole, uiMode } = useRBACStore()
  const location = useLocation()
  const [isProfileSettingsOpen, setIsProfileSettingsOpen] = React.useState(false)

  const userData = React.useMemo(() => {
    if (!user) return null
    return {
      name: user.attributes?.given_name || user.sub,
      email: user.attributes?.email || user.sub,
      avatar: user.attributes?.picture || "",
      role: userRole || undefined,
    }
  }, [user, userRole])

  // Filter menu items based on user role and UI mode
  const filteredMenuItems = React.useMemo(() => {
    return filterMenuByRoleAndUIMode(
      data.navMain, 
      userRole, 
      uiMode,
      rbacLoading || (!userRole && !!user),
      user?.attributes?.email
    );
  }, [userRole, uiMode, rbacLoading, user])

  // Helper function to check if a menu item is active
  const isActiveItem = (itemUrl: string, subItemUrl?: string) => {
    if (subItemUrl) {
      // For sub-items, check if the current path matches the sub-item URL
      if (subItemUrl.includes('?')) {
        // Handle URLs with query parameters (like assessment tabs)
        const [path, query] = subItemUrl.split('?')
        const currentPath = location.pathname
        const currentQuery = location.search.replace('?', '')
        return path === currentPath && query === currentQuery
      }
      return location.pathname + location.search === subItemUrl
    }
    // For main items, check if the current path starts with the item URL
    return itemUrl !== "#" && location.pathname === itemUrl
  }

  return (
    <Sidebar {...props} className="bg-white border-r border-gray-200">
      <SidebarHeader className="border-b border-gray-200 bg-gray-50/80">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <Link to="/dashboard" className="flex items-center justify-center p-4 hover:bg-white/50 transition-colors duration-150">
                <img
                  src={tmLogo}
                  alt="TM Logo"
                  className="h-10 w-auto transition-transform hover:scale-105 drop-shadow-sm"
                />
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent className="py-4">
        <SidebarGroup>
          <SidebarMenu>
            {filteredMenuItems.map((item) => (
              <SidebarMenuItem key={item.titleKey}>
                {item.url !== "#" ? (
                  item.titleKey === "guideline.title" ? (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <SidebarMenuButton 
                            asChild 
                            isActive={isActiveItem(item.url)}
                            className={cn(
                              "w-full px-4 py-3 text-gray-800 font-semibold transition-all duration-150",
                              "rounded-lg",
                              "flex items-center gap-3",
                              "border-l-4 border-transparent",
                              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2",
                              "hover:bg-teal-600 hover:text-white hover:shadow-md",
                              "active:bg-teal-700 active:shadow-inner",
                              "hover:border-teal-500",
                              isActiveItem(item.url) && "bg-teal-600 text-white border-teal-500 shadow-md"
                            )}>
                            <Link to={item.url} className="flex-1">{t(item.titleKey)}</Link>
                          </SidebarMenuButton>
                        </TooltipTrigger>
                        <TooltipContent side="right" align="center">
                          {t("guideline.optional")}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ) : (
                    <SidebarMenuButton 
                      asChild 
                      isActive={isActiveItem(item.url)}
                      className={cn(
                        "w-full px-4 py-3 text-gray-800 font-semibold transition-all duration-150",
                        "rounded-lg",
                        "flex items-center gap-3",
                        "border-l-4 border-transparent",
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2",
                        "hover:bg-teal-600 hover:text-white hover:shadow-md",
                        "active:bg-teal-700 active:shadow-inner",
                        "hover:border-teal-500",
                        isActiveItem(item.url) && "bg-teal-600 text-white border-teal-500 shadow-md"
                      )}>
                      <Link to={item.url} className="flex-1">{t(item.titleKey)}</Link>
                    </SidebarMenuButton>
                  )
                ) : (
                  <SidebarMenuButton 
                    isActive={item.items?.some(subItem => isActiveItem(item.url, subItem.url))}
                    className={cn(
                      "w-full px-4 py-3 text-gray-800 font-semibold transition-all duration-150",
                      "rounded-lg",
                      "flex items-center gap-3",
                      "border-l-4 border-transparent",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2",
                      item.items?.some(subItem => isActiveItem(item.url, subItem.url)) && "bg-teal-600 text-white border-teal-500 shadow-md"
                    )}>
                    <span className="flex-1">{t(item.titleKey)}</span>
                  </SidebarMenuButton>
                )}
                {item.items?.length ? (
                  <SidebarMenuSub className="ml-4 mt-1 space-y-1">
                    {item.items.map((subItem) => (
                      <SidebarMenuSubItem key={subItem.titleKey}>
                        <SidebarMenuSubButton 
                          asChild={subItem.url !== "#"}
                          isActive={isActiveItem(item.url, subItem.url)}
                          className={cn(
                            "w-full px-4 py-2 text-gray-700 font-medium transition-all duration-150",
                            "rounded-lg",
                            "flex items-center gap-3",
                            "border-l-2 border-transparent",
                            "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-teal-500 focus-visible:ring-offset-1",
                            subItem.url !== "#" && [
                              "hover:bg-teal-500/10 hover:text-teal-700 hover:shadow-sm",
                              "active:bg-teal-500/20 active:shadow-inner",
                              "hover:border-teal-400"
                            ],
                            isActiveItem(item.url, subItem.url) && "bg-teal-500/20 text-teal-700 border-teal-400 shadow-sm"
                          )}
                        >
                          {subItem.url !== "#" ? (
                            <Link to={subItem.url} className="flex-1 flex items-center gap-2">
                              {t(subItem.titleKey)}
                              {subItem.icon}
                            </Link>
                          ) : (
                            <span className="flex-1 flex items-center gap-2">
                              {t(subItem.titleKey)}
                              {subItem.icon}
                            </span>
                          )}
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    ))}
                  </SidebarMenuSub>
                ) : null}
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>
      {userData && (
        <div className="mt-auto border-t border-gray-200 bg-gray-50/50">
          <div className="p-4">
            <NavUser 
              user={userData} 
              onLogout={logout}
              onProfileSettings={() => setIsProfileSettingsOpen(true)}
            />
          </div>
        </div>
      )}
      <SidebarRail />
      <ProfileSettingsOverlay 
        open={isProfileSettingsOpen}
        onOpenChange={setIsProfileSettingsOpen}
      />
    </Sidebar>
  )
}
